import { useLayoutEffect, type ReactNode } from 'react';
import { Anchor, Box, Container, Group, Stack, Text } from '@mantine/core';
import { IconLock } from '@tabler/icons-react';
import { CustomerLogo } from '@/components/customer/CustomerLogo';
import { CustomerAppearanceMenu } from '@/components/customer/CustomerAppearanceMenu';

interface CustomerLayoutProps {
  children: ReactNode;
}

export default function CustomerLayout({ children }: CustomerLayoutProps) {
  useLayoutEffect(() => {
    const root = document.documentElement;
    const previousTitle = document.title;
    const previouslyBranded = root.hasAttribute('data-customer-portal');

    root.setAttribute('data-customer-portal', '');
    document.title = 'ScalePad | Secure Proposal';

    return () => {
      if (!previouslyBranded) {
        root.removeAttribute('data-customer-portal');
      }
      document.title = previousTitle;
    };
  }, []);

  return (
    <Box
      mih="100vh"
      bg="var(--surface-base)"
      style={{ display: 'flex', flexDirection: 'column' }}
    >
      <Box
        component="header"
        bg="var(--surface-raised)"
        py={{ base: 22, sm: 28 }}
        style={{
          borderBottom: '1px solid var(--border-subtle)',
        }}
      >
        <Container size={1232}>
          <Group justify="space-between" align="center" wrap="nowrap">
            <Anchor
              href="https://www.scalepad.com"
              target="_blank"
              rel="noreferrer"
              underline="never"
              c="var(--text-primary)"
              aria-label="Visit ScalePad.com"
            >
              <CustomerLogo />
            </Anchor>
            <Group gap="sm" wrap="nowrap">
              <Group gap={6} wrap="nowrap" c="var(--text-secondary)">
                <IconLock size={14} aria-hidden />
                <Text size="xs" fw={600} c="inherit" visibleFrom="sm" style={{ whiteSpace: 'nowrap' }}>
                  Secure ScalePad portal
                </Text>
              </Group>
              <CustomerAppearanceMenu />
            </Group>
          </Group>
        </Container>
      </Box>

      <Box
        component="main"
        className="customer-portal-content"
        style={{ flex: 1 }}
        py={{ base: 24, sm: 40 }}
      >
        <Container size={1232}>{children}</Container>
      </Box>

      <Box
        component="footer"
        bg="var(--surface-sunken)"
        py="lg"
        style={{ borderTop: '1px solid var(--border-subtle)' }}
      >
        <Container size={1232}>
          <Stack gap="sm" align="center">
            <CustomerLogo size="sm" />
            <Group gap={6} c="var(--text-secondary)">
              <IconLock size={13} aria-hidden />
              <Text size="xs" c="inherit" fw={500} ta="center">
                Secure quote and checkout experience
              </Text>
            </Group>
            <Text size="xs" c="dimmed" ta="center">
              Questions? Reply to the email that delivered this proposal.
            </Text>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}
